import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ScrollArea,
  toast,
} from "@devhop/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { formsApi } from "@/modules/forms/presentation/api/client";
import { useSubmitAdminFormResponseMutation } from "@/modules/forms/presentation/api/queries";
import type { FormFieldDTO } from "@/modules/forms/presentation/api/types";
import {
  initRepeaterValsForFields,
} from "@/modules/forms/presentation/public/PublicFormRepeaterBlock";
import type { RepeaterAnswer } from "@/modules/forms/domain/lib/form-repeater-settings";
import {
  buildAnswersPayload,
  collectClientFieldErrors,
  PublicFormFieldBlock,
} from "@/modules/forms/presentation/public/PublicFormPage";
import { publicSubmitFieldMessage } from "@/modules/forms/presentation/public/public-forms-api";
import type {
  PublicFormFieldDTO,
  PublicFormResponseDTO,
} from "@/modules/forms/presentation/public/types";
import { uploadFileToPresignedUrl } from "@/shared/lib/upload-to-presigned";

type AdminAddResponseDialogProps = {
  formId: string;
  formTitle: string;
  fields: FormFieldDTO[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function toPublicFields(fields: FormFieldDTO[]): PublicFormFieldDTO[] {
  return [...fields]
    .sort((a, b) => a.order - b.order)
    .map((f) => ({
      id: f.id,
      type: f.type,
      label: f.label,
      description: f.description,
      placeholder: f.placeholder,
      required: f.required,
      options: f.options,
      settings: f.settings,
      order: f.order,
    }));
}

function resetFormState(ordered: PublicFormFieldDTO[]) {
  const nextCheck: Record<string, string[]> = {};
  for (const f of ordered) {
    if (f.type === "checkbox") nextCheck[f.id] = [];
  }
  return {
    textVals: {} as Record<string, string>,
    checkVals: nextCheck,
    repeaterVals: initRepeaterValsForFields(ordered),
    fileVals: {} as Record<string, string>,
    fileBusy: {} as Record<string, boolean>,
    fieldErrors: {} as Record<string, string>,
  };
}

export function AdminAddResponseDialog({
  formId,
  formTitle,
  fields,
  open,
  onOpenChange,
}: AdminAddResponseDialogProps) {
  const orderedFields = useMemo(() => toPublicFields(fields), [fields]);
  const answerableCount = useMemo(
    () => orderedFields.filter((f) => f.type !== "section").length,
    [orderedFields],
  );

  const [textVals, setTextVals] = useState<Record<string, string>>({});
  const [checkVals, setCheckVals] = useState<Record<string, string[]>>({});
  const [fileVals, setFileVals] = useState<Record<string, string>>({});
  const [fileBusy, setFileBusy] = useState<Record<string, boolean>>({});
  const [repeaterVals, setRepeaterVals] = useState<
    Record<string, RepeaterAnswer>
  >({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const submitMut = useSubmitAdminFormResponseMutation(formId);

  useEffect(() => {
    if (!open) return;
    const next = resetFormState(orderedFields);
    setTextVals(next.textVals);
    setCheckVals(next.checkVals);
    setRepeaterVals(next.repeaterVals);
    setFileVals(next.fileVals);
    setFileBusy(next.fileBusy);
    setFieldErrors(next.fieldErrors);
  }, [open, orderedFields]);

  const clearFieldError = useCallback((fieldId: string) => {
    setFieldErrors((prev) => {
      if (!(fieldId in prev)) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  }, []);

  const setFieldErrorOne = useCallback((fieldId: string, message: string) => {
    setFieldErrors((prev) => ({ ...prev, [fieldId]: message }));
  }, []);

  const uploadFile = useCallback(
    async (fid: string, fieldId: string, file: File) => {
      const { uploadUrl, key } = await formsApi.presignAdminResponseFile(fid, {
        fieldId,
        filename: file.name,
        contentType: file.type || undefined,
        sizeBytes: file.size,
      });
      await uploadFileToPresignedUrl(uploadUrl, file, {
        contentType: file.type || undefined,
      });
      return key;
    },
    [],
  );

  const pseudoDetail = useMemo((): PublicFormResponseDTO => {
    return {
      id: formId,
      title: formTitle,
      description: null,
      settings: {},
      fields: orderedFields,
      submission: { open: true },
    };
  }, [formId, formTitle, orderedFields]);

  function handleSubmit() {
    setFieldErrors({});
    const clientErrs = collectClientFieldErrors(
      orderedFields,
      textVals,
      checkVals,
      fileVals,
      repeaterVals,
    );
    if (Object.keys(clientErrs).length > 0) {
      setFieldErrors(clientErrs);
      return;
    }
    const payload = buildAnswersPayload(
      pseudoDetail,
      textVals,
      checkVals,
      fileVals,
      repeaterVals,
    );
    submitMut.mutate(payload, {
      onSuccess: () => {
        toast.success("ເພີ່ມຄຳຕອບສຳເລັດ");
        onOpenChange(false);
      },
      onError: (e) => {
        const err = e as Error & { code?: string; fieldId?: string };
        if (err.fieldId) {
          setFieldErrors({
            [err.fieldId]: publicSubmitFieldMessage(err.code ?? "UNKNOWN"),
          });
          return;
        }
        toast.error("ເພີ່ມຄຳຕອບບໍ່ສຳເລັດ");
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton
        className="flex max-h-[min(90vh,720px)] min-h-0 flex-col gap-0 overflow-hidden p-0 sm:max-w-xl"
      >
        <DialogHeader className="shrink-0 border-b px-6 py-4">
          <DialogTitle>ເພີ່ມຄຳຕອບ (admin)</DialogTitle>
          <DialogDescription>
            ບັນທຶກຄຳຕອບໃຫ້ຟອມ「{formTitle}」ໂດຍບໍ່ຂຶ້ນກັບສະຖານະເປີດ/ປິດ
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[calc(min(90vh,720px)-10rem)] min-h-0 w-full flex-1 overflow-auto px-6 pr-4">
          <div className="py-4">
            {answerableCount === 0 ? (
              <p className="py-8 text-center text-muted-foreground text-sm">
                ຍັງບໍ່ມີຄຳຖາມໃນຟອມ — ເພີ່ມຟິວກ່ອນຈຶ່ງບັນທຶກຄຳຕອບໄດ້
              </p>
            ) : (
              <div className="space-y-6">
                {orderedFields.map((f) => (
                  <PublicFormFieldBlock
                    key={f.id}
                    formId={formId}
                    field={f}
                    disabled={submitMut.isPending}
                    fieldError={fieldErrors[f.id]}
                    clearFieldError={clearFieldError}
                    setFieldError={setFieldErrorOne}
                    textVals={textVals}
                    setTextVals={setTextVals}
                    checkVals={checkVals}
                    setCheckVals={setCheckVals}
                    fileVals={fileVals}
                    setFileVals={setFileVals}
                    fileBusy={fileBusy}
                    setFileBusy={setFileBusy}
                    repeaterVals={repeaterVals}
                    setRepeaterVals={setRepeaterVals}
                    uploadFile={uploadFile}
                  />
                ))}
              </div>
            )}
          </div>
        </ScrollArea>
        <DialogFooter className="shrink-0 border-t px-6 py-4">
          <Button
            type="button"
            variant="outline"
            disabled={submitMut.isPending}
            onClick={() => onOpenChange(false)}
          >
            ຍົກເລີກ
          </Button>
          <Button
            type="button"
            disabled={submitMut.isPending || answerableCount === 0}
            onClick={handleSubmit}
          >
            {submitMut.isPending ? "ກຳລັງບັນທຶກ…" : "ບັນທຶກຄຳຕອບ"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
